import type { GridCell, WinResult } from '@core/types/cascade-slot.types';

export interface FallMap {
  [key: string]: number; // "row-col" -> distance fallen
}

/** Check whether a cell position is within a reel's active area */
function isCellInReel(row: number, col: number, totalRows: number, reelHeights?: number[]): boolean {
  if (!reelHeights || !reelHeights[col]) return true;
  const startRow = totalRows - reelHeights[col];
  return row >= startRow;
}

export function removeWinningSymbols(
  grid: GridCell[][],
  wins: WinResult[],
): (GridCell | null)[][] {
  const result: (GridCell | null)[][] = grid.map(row => row.map(cell => ({ ...cell })));

  for (const win of wins) {
    for (const pos of win.positions) {
      result[pos.row][pos.col] = null;
    }
  }

  return result;
}

export function applyGravity(
  grid: (GridCell | null)[][],
  direction: 'vertical' | 'horizontal',
  reelHeights?: number[],
): (GridCell | null)[][] {
  const rows = grid.length;
  const cols = grid[0]?.length || 0;
  const result: (GridCell | null)[][] = grid.map(row => [...row]);

  if (direction === 'horizontal') {
    // Horizontal gravity: symbols shift RIGHT per row, empty spaces end up on LEFT
    // Port from model.ts CheckTumble
    for (let i = 0; i < rows; i++) {
      for (let j = cols - 2; j >= 0; j--) {
        if (!isCellInReel(i, j, rows, reelHeights)) continue;
        if (result[i][j] !== null) {
          // Find rightmost null position where jj > j (within reel bounds)
          for (let jj = cols - 1; jj > j; jj--) {
            if (!isCellInReel(i, jj, rows, reelHeights)) continue;
            if (result[i][jj] === null) {
              result[i][jj] = result[i][j];
              result[i][j] = null;
              break;
            }
          }
        }
      }
    }
  } else {
    // Vertical gravity: symbols fall DOWN per column, empty spaces on TOP
    for (let j = 0; j < cols; j++) {
      const startRow = reelHeights ? rows - (reelHeights[j] ?? rows) : 0;
      for (let i = rows - 2; i >= startRow; i--) {
        if (result[i][j] !== null) {
          for (let ii = rows - 1; ii > i; ii--) {
            if (result[ii][j] === null) {
              result[ii][j] = result[i][j];
              result[i][j] = null;
              break;
            }
          }
        }
      }
    }
  }

  return result;
}

export function applyGravityWithTracking(
  grid: (GridCell | null)[][],
  direction: 'vertical' | 'horizontal',
  reelHeights?: number[],
): { result: (GridCell | null)[][]; fallMap: FallMap } {
  const rows = grid.length;
  const cols = grid[0]?.length || 0;
  const result: (GridCell | null)[][] = grid.map(row => [...row]);
  const fallMap: FallMap = {};

  // Track original positions of non-null cells
  const originalPositions = new Map<GridCell, { row: number; col: number }>();
  for (let i = 0; i < rows; i++) {
    for (let j = 0; j < cols; j++) {
      if (result[i][j] !== null) {
        originalPositions.set(result[i][j]!, { row: i, col: j });
      }
    }
  }

  if (direction === 'horizontal') {
    for (let i = 0; i < rows; i++) {
      for (let j = cols - 2; j >= 0; j--) {
        if (!isCellInReel(i, j, rows, reelHeights)) continue;
        if (result[i][j] !== null) {
          for (let jj = cols - 1; jj > j; jj--) {
            if (!isCellInReel(i, jj, rows, reelHeights)) continue;
            if (result[i][jj] === null) {
              result[i][jj] = result[i][j];
              result[i][j] = null;
              break;
            }
          }
        }
      }
    }
  } else {
    for (let j = 0; j < cols; j++) {
      const startRow = reelHeights ? rows - (reelHeights[j] ?? rows) : 0;
      for (let i = rows - 2; i >= startRow; i--) {
        if (result[i][j] !== null) {
          for (let ii = rows - 1; ii > i; ii--) {
            if (result[ii][j] === null) {
              result[ii][j] = result[i][j];
              result[i][j] = null;
              break;
            }
          }
        }
      }
    }
  }

  // Calculate distances
  for (let i = 0; i < rows; i++) {
    for (let j = 0; j < cols; j++) {
      const cell = result[i][j];
      if (cell !== null && originalPositions.has(cell)) {
        const orig = originalPositions.get(cell)!;
        const dist = direction === 'horizontal'
          ? j - orig.col
          : i - orig.row;
        if (dist > 0) {
          fallMap[`${i}-${j}`] = dist;
        }
      }
    }
  }

  return { result, fallMap };
}

export function clearAnimationFlags(grid: GridCell[][]): GridCell[][] {
  return grid.map((row, rowIdx) =>
    row.map((cell, colIdx) => ({
      ...cell,
      isNew: false,
      isFalling: false,
      fallDistance: 0,
      animationTrigger: null,
    }))
  );
}

export function markNewCells(grid: GridCell[][]): GridCell[][] {
  return grid.map(row =>
    row.map(cell => ({
      ...cell,
      isNew: false,
    }))
  );
}
